/*
	critical_init.js

	Why this file exists:
	- The main frontend app bundle is loaded in the footer.
	- Instead of `newpageshown`, we use `newpageshownfast`.
	- `newpageshown` is triggered in `app.js`, and `app.js` is loaded in the footer. Waiting for that can be too slow on initial page load.
	- `newpageshownfast` is emitted directly in the HTML near `</body>`, so it triggers earlier for initial page load.
	- This tiny head-loaded script reacts to that event immediately, so the first visible media can start loading before the full app initializes.

	What this file intentionally does:
	- Only load media above the fold (initial viewport) for initial page render.
	- Handle plain images, html5 videos, and first carousel slides in the viewport.
	- Kick off Pretext lazy chunks (text line-split) before frontend.app — `newpageshownfast` runs before wp_footer.
	- Keep logic dependency-light (vanilla JS, no jQuery dependency for this bundle).
	- Run only once on initial page load (guarded by `hasRunInitialFastLoad`).

	What this file intentionally does NOT do:
	- No global `content_ready` emit (would trigger many unrelated modules too early).
	- No full page/overlay lifecycle handling (that remains in frontend.app and existing controllers).
	- No below-the-fold loading (normal runtime flow handles that later).
	- No repeated handling for later navigations. After initial load, the normal frontend app JS is already loaded and handles subsequent image/video lazyloading on page changes.
*/
var hasRunInitialFastLoad = false
var CRITICAL_INIT_DEBUG = false
var layCriticalInitData = window.layCriticalInitData || {}
var CRITICAL_INIT_VIEWPORT_OFFSET_PX = 100

var debugLog = function () {
	if (!CRITICAL_INIT_DEBUG || typeof console === 'undefined' || typeof console.log !== 'function') {
		return
	}
	console.log.apply(console, arguments)
}

var isInsideViewport = function (el) {
	if (!el || typeof el.getBoundingClientRect !== 'function') {
		return false
	}
	var rect = el.getBoundingClientRect()
	return rect.bottom > 0 && rect.top < window.innerHeight + CRITICAL_INIT_VIEWPORT_OFFSET_PX && rect.right > 0 && rect.left < window.innerWidth
}

var roundUpSizes = function (w) {
	var sizes = [265, 512, 768, 1024, 1280, 1920, 2560, 3200, 3840, 4096]
	var width = w
	for (var i = 0; i < sizes.length; i++) {
		if (sizes[i] >= w) {
			width = sizes[i]
			break
		}
	}
	return width
}

var setImageSizesAttributeForImg = function (img) {
	if (!img || typeof img.getBoundingClientRect !== 'function') {
		return
	}

	var w = img.getBoundingClientRect().width
	var objectfit = window.getComputedStyle ? window.getComputedStyle(img).objectFit : ''

	if (w === 0) {
		var ar = parseFloat(img.getAttribute('data-ar'))
		var h = img.clientHeight
		if (!Number.isNaN(ar) && ar !== 0 && h > 0) {
			w = h / ar
		}
	}

	var parentFit = img.getAttribute('data-parent-fit')
	if (parentFit === 'cover' || objectfit === 'cover') {
		var dataW = parseInt(img.getAttribute('data-w'), 10)
		var dataH = parseInt(img.getAttribute('data-h'), 10)

		if (!Number.isNaN(dataW) && !Number.isNaN(dataH) && dataH !== 0) {
			var rect = img.parentNode ? img.parentNode.getBoundingClientRect() : null
			if (rect && rect.width === 0 && rect.height === 0 && img.parentNode && img.parentNode.parentNode) {
				rect = img.parentNode.parentNode.getBoundingClientRect()
			}
			if (rect && rect.height > 0) {
				if (dataW / dataH < rect.width / rect.height) {
					w = rect.width
				} else {
					var imgH = rect.height
					w = (dataW / dataH) * imgH
				}
			}
		}
	}

	if (w && w > 0) {
		var rounded = roundUpSizes(w)
		img.setAttribute('sizes', rounded + 'px')
		img.setAttribute('data-sizes', rounded + 'px')
	}
}

var isPlaceholderImageSrc = function (src) {
	return typeof src === 'string' && src.indexOf('data:image') === 0
}

var getActiveImageSrc = function (img) {
	return img.currentSrc || img.src || img.getAttribute('src') || ''
}

var hasLoadedRealImage = function (img) {
	return img.complete && img.naturalWidth > 0 && !isPlaceholderImageSrc(getActiveImageSrc(img))
}

var getScreenSize = function () {
	var phoneBreakpoint = parseInt(layCriticalInitData.breakpoint, 10)

	// Fallbacks in case the payload is missing for some reason.
	if (Number.isNaN(phoneBreakpoint)) {
		phoneBreakpoint = 480
	}

	return window.innerWidth <= phoneBreakpoint ? 'phone' : 'desktop'
}

var getInitialTarget = function () {
	var screenSize = getScreenSize()
	debugLog('[critical_init] screen size:', screenSize)

	// Initial fast pass only targets main page content (no overlays).
	if (screenSize === 'desktop') {
		return document.querySelectorAll('#grid, .cover-region-desktop, #footer')
	}

	var selectors = []
	if (document.querySelector('body>.lay-content #custom-phone-grid')) {
		selectors.push('#custom-phone-grid', '.cover-region-phone')
	} else {
		selectors.push('#grid', '.cover-region-desktop')
	}

	if (document.querySelector('body>.lay-content #footer-custom-phone-grid')) {
		selectors.push('#footer-custom-phone-grid')
	} else {
		selectors.push('#footer')
	}

	return document.querySelectorAll(selectors.join(', '))
}

var loadImagesForTarget = function (targets) {
	if (!targets || targets.length === 0) {
		debugLog('[critical_init] no targets for image loading')
		return
	}

	var loadedCount = 0
	var lazyLoadedCount = 0
	var loadingMode = layCriticalInitData.image_loading || 'lazy_load'

	var markImageLoaded = function (img, isError) {
		// Class semantics requested for this fast path:
		// - Ensure `lazyload` and `lazyloaded` are present
		// - Remove `lazyloading` (the "in-progress" state)
		// - On error add `loaded-error`
		img.classList.remove('lazyloading')
		img.classList.add('lazyload')

		// reset states
		img.classList.remove('lazyloaded')
		img.classList.remove('loaded-error')

		if (isError) {
			img.classList.add('loaded-error')
		} else {
			img.classList.add('lazyloaded')
			// Some code paths also listen for "loaded" (outside of vanilla-lazyload)
			img.classList.add('loaded')
		}
	}

	var markImageWasProcessed = function (img) {
		img.setAttribute('data-was-processed', 'true')
	}

	targets.forEach(function (target) {
		target.querySelectorAll('img.setsizes, img.lay-gif, img.lay-image-original, img.lazyload, img.lay-image-responsive').forEach(function (img) {
			if (!isInsideViewport(img)) {
				return
			}

			if (loadingMode === 'lazy_load') {
				var fallbackSrcset = img.getAttribute('data-srcset')
				var fallbackSrc = img.getAttribute('data-src')
				if (fallbackSrcset || img.classList.contains('setsizes') || img.classList.contains('lay-image-responsive') || img.classList.contains('lazyload')) {
					setImageSizesAttributeForImg(img)
				}
				if (fallbackSrcset) {
					img.setAttribute('srcset', fallbackSrcset)
				}
				// Avoid double-fetch for responsive images:
				// if srcset exists, let the browser pick only from srcset candidates.
				// Original images and GIFs can intentionally start with the smallest
				// generated thumbnail as their blur placeholder, so their data-src must
				// replace any existing src, not only data-URI placeholders.
				var shouldLoadDirectSrc = img.classList.contains('lay-image-original') || img.classList.contains('lay-gif')
				if (!fallbackSrcset && fallbackSrc && (shouldLoadDirectSrc || !img.getAttribute('src') || isPlaceholderImageSrc(img.getAttribute('src')))) {
					img.setAttribute('src', fallbackSrc)
				}

				// Mark as loaded (or error) without relying on vanilla-lazyload's internal API.
				if (hasLoadedRealImage(img)) {
					markImageWasProcessed(img)
					markImageLoaded(img, false)
				} else if (img.complete && !isPlaceholderImageSrc(getActiveImageSrc(img))) {
					// naturalWidth avoids marking "broken" cached images as loaded
					markImageWasProcessed(img)
					markImageLoaded(img, true)
				} else {
					img.addEventListener(
						'load',
						function () {
							if (hasLoadedRealImage(img)) {
								markImageWasProcessed(img)
								markImageLoaded(img, false)
							}
						},
						{ once: true }
					)
					img.addEventListener(
						'error',
						function () {
							markImageWasProcessed(img)
							markImageLoaded(img, true)
						},
						{ once: true }
					)
				}

				lazyLoadedCount++
				return
			}

			if (img.classList.contains('setsizes') || img.classList.contains('lay-image-responsive')) {
				var srcset = img.getAttribute('data-srcset')
				setImageSizesAttributeForImg(img)
				if (srcset) {
					img.setAttribute('srcset', srcset)
				}
				var src = img.getAttribute('data-src')
				// Avoid forcing src when srcset exists to prevent duplicate image loads.
				if (!srcset && src && !img.getAttribute('src')) {
					img.setAttribute('src', src)
				}
			} else {
				var src = img.getAttribute('data-src')
				if (src) {
					img.setAttribute('src', src)
				}
			}
			loadedCount++
		})
	})

	debugLog('[critical_init] image mode:', loadingMode, '| loaded instant:', loadedCount, '| triggered lazyload:', lazyLoadedCount)
}

var loadVideoElement = function (video) {
	for (var i = 0; i < video.children.length; i++) {
		var child = video.children[i]
		if (child && child.tagName === 'SOURCE' && child.dataset && child.dataset.src != undefined) {
			child.src = child.dataset.src
		}
	}

	video.setAttribute('preload', 'auto')
	try {
		video.load()
	} catch (e) {}

	video.classList.remove('video-lazyload')
	video.classList.add('loaded')
	if (video.classList.contains('autoplay')) {
		video.setAttribute('autoplay', '')
	}
}

var loadCarouselImageElement = function (img) {
	if (!img) {
		return
	}

	if (img.classList.contains('carousel-original-img')) {
		var originalSrc = img.getAttribute('data-lay-src')
		if (originalSrc) {
			img.setAttribute('src', originalSrc)
		}
	} else if (img.classList.contains('carousel-responsive-img')) {
		var srcset = img.getAttribute('data-lay-srcset')
		var src = img.getAttribute('data-lay-src')
		setImageSizesAttributeForImg(img)
		if (srcset) {
			img.setAttribute('srcset', srcset)
		}
		// For responsive carousel images prefer srcset-only to avoid extra request.
		if (!srcset && src) {
			img.setAttribute('src', src)
		}
	}

	if (img.complete) {
		img.classList.add('loaded')
	} else {
		img.addEventListener(
			'load',
			function () {
				img.classList.add('loaded')
			},
			{ once: true }
		)
		img.addEventListener(
			'error',
			function () {
				img.classList.add('loaded')
			},
			{ once: true }
		)
	}
}

var loadCarouselMediaForTarget = function (targets) {
	if (!targets || targets.length === 0) {
		debugLog('[critical_init] no targets for carousel media loading')
		return
	}

	var visibleCarouselCount = 0
	var loadedCarouselImageCount = 0
	var loadedCarouselVideoCount = 0
	targets.forEach(function (target) {
		target.querySelectorAll('.lay-carousel').forEach(function (carousel) {
			if (!isInsideViewport(carousel)) {
				return
			}
			visibleCarouselCount++

			// Match plugin behavior: eagerly load first slide image (+ second as buffer).
			var slideImages = carousel.querySelectorAll('.lay-carousel-slide img.carousel-original-img, .lay-carousel-slide img.carousel-responsive-img')
			for (var i = 0; i < slideImages.length && i < 2; i++) {
				loadCarouselImageElement(slideImages[i])
				loadedCarouselImageCount++
			}

			// If first visible slides contain html5 videos, load those too.
			var lazyVideos = carousel.querySelectorAll('.lay-carousel-slide video.video-lazyload')
			for (var j = 0; j < lazyVideos.length; j++) {
				if (isInsideViewport(lazyVideos[j])) {
					loadVideoElement(lazyVideos[j])
					loadedCarouselVideoCount++
				}
			}
		})
	})
	debugLog('[critical_init] visible carousels:', visibleCarouselCount, '| loaded carousel images:', loadedCarouselImageCount, '| loaded carousel videos:', loadedCarouselVideoCount)
}

var loadHtml5VideosForTarget = function (targets) {
	if (!targets || targets.length === 0) {
		debugLog('[critical_init] no targets for html5 video loading')
		return
	}

	var loadedVideoCount = 0
	targets.forEach(function (target) {
		target.querySelectorAll('video.video-lazyload:not(.video-inside-thumbnailgrid-with-filter)').forEach(function (video) {
			if (isInsideViewport(video)) {
				loadVideoElement(video)
				loadedVideoCount++
			}
		})
	})
	debugLog('[critical_init] loaded above-the-fold html5 videos:', loadedVideoCount)
}

// Pretext (masked/fade lines & words): same script IDs/URLs as pretext_lazy.js — must stay in sync.
var PRETEXT_LAZY_ANIMATION_TYPES = {
	masked_lines: true,
	fade_lines: true,
	masked_words: true,
	fade_words: true,
	fade_characters: true,
	typewriter_lines: true,
	typewriter_words: true,
}

var needsPretextLazyFromDom = function () {
	var nodes = document.querySelectorAll('.lay-text-animation[data-animation-type]')
	for (var i = 0; i < nodes.length; i++) {
		var t = nodes[i].getAttribute('data-animation-type')
		if (t && PRETEXT_LAZY_ANIMATION_TYPES[t]) {
			return true
		}
	}
	return false
}

var hyphenationScriptIdFromFilename = function (filename) {
	var map = {
		'pretext_hyphenation_germanic.min.js': 'lay-pretext-hyphenation-germanic',
		'pretext_hyphenation_roman.min.js': 'lay-pretext-hyphenation-roman',
		'pretext_hyphenation_en.min.js': 'lay-pretext-hyphenation-en',
	}
	return map[filename] || 'lay-pretext-hyphenation-en'
}

var appendScriptOnce = function (id, src, onload, onerror) {
	if (document.getElementById(id)) {
		if (onload) {
			onload()
		}
		return
	}
	var s = document.createElement('script')
	s.id = id
	s.src = src
	s.async = true
	s.onload = onload
	s.onerror =
		onerror ||
		function () {
			debugLog('[critical_init] pretext lazy: script error', src)
		}
	document.body.appendChild(s)
}

var appendAssetVersion = function (src, version) {
	if (!version) {
		return src
	}
	return src + (src.indexOf('?') === -1 ? '?' : '&') + 'ver=' + encodeURIComponent(version)
}

var waitForPretextApi = function (done, timeoutMs) {
	var start = Date.now()
	var tick = function () {
		if (window.layPretextTextAnimation && typeof window.layPretextTextAnimation.registerHyphenationPatterns === 'function') {
			done(true)
			return
		}
		if (Date.now() - start > timeoutMs) {
			done(false)
			return
		}
		requestAnimationFrame(tick)
	}
	tick()
}

/** Same network requests as pretext_lazy.ensureLayPretextTextAnimationLoaded; runs ~one main-bundle frame earlier. */
var runPretextLazyPrefetchEarly = function () {
	var d = layCriticalInitData || {}
	if (!d.template_uri || !d.pretext_hyphenation_js) {
		return
	}
	if (!needsPretextLazyFromDom()) {
		return
	}
	if (window.layPretextTextAnimation && typeof window.layPretextTextAnimation.registerHyphenationPatterns === 'function') {
		return
	}
	var base = String(d.template_uri).replace(/\/?$/, '') + '/frontend/assets/js/'
	var coreSrc = appendAssetVersion(base + 'pretext_textanimation.min.js', d.asset_version)
	var hypSrc = appendAssetVersion(base + d.pretext_hyphenation_js, d.asset_version)
	var hypId = hyphenationScriptIdFromFilename(d.pretext_hyphenation_js)

	debugLog('[critical_init] pretext lazy prefetch: core +', d.pretext_hyphenation_js)

	appendScriptOnce('lay-pretext-textanimation', coreSrc, function () {
		waitForPretextApi(function (ok) {
			if (!ok) {
				debugLog('[critical_init] pretext lazy: API missing after core load')
				return
			}
			appendScriptOnce(hypId, hypSrc, function () {
				debugLog('[critical_init] pretext lazy: hyphenation loaded')
			})
		}, 15000)
	})
}

var runInitialFastMediaLoad = function () {
	if (hasRunInitialFastLoad) {
		debugLog('[critical_init] skip, already ran once')
		return
	}
	hasRunInitialFastLoad = true
	debugLog('[critical_init] run initial fast media load')

	runPretextLazyPrefetchEarly()

	var targets = getInitialTarget()
	debugLog('[critical_init] target containers:', targets.length)
	loadImagesForTarget(targets)
	loadHtml5VideosForTarget(targets)
	loadCarouselMediaForTarget(targets)
	debugLog('[critical_init] done')
}

window.laytheme.on('newpageshownfast', runInitialFastMediaLoad)
